/* ============================================================
   234LABS — theme.js  (entry, ES module)
   ============================================================ */
/* Bare specifiers, mapped to versioned URLs by the <script type="importmap">
   in layout/theme.liquid. A relative './motion.js' here would be fetched
   WITHOUT Shopify's ?v= buster and could be served stale by the CDN. */
import { initCart } from '@t/cart';
import { initMenu } from '@t/menu';
import { initSearch } from '@t/search';
import { initCollection } from '@t/collection';
import { initMotion } from '@t/motion';

export const routes = window.__routes || {};
export const shopSettings = window.__shop || {};

/* ---------- money ----------
   Mirrors Shopify's money_format token replacement. */
export function formatMoney(cents, format) {
  const fmt = format || shopSettings.moneyFormat || '${{amount}}';
  const value = (cents / 100).toFixed(2);
  // Mirrors the `money_without_trailing_zeros` Liquid filter used for every
  // server-rendered price: drop ".00" on whole amounts, keep real decimals.
  const isWhole = Number(cents) % 100 === 0;

  const withGroups = (str, decimals, thousands) => {
    const [int, dec = ''] = value.split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const d = isWhole ? 0 : decimals;
    return d === 0 ? grouped : `${grouped}${d}${dec}`;
  };

  const out = fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
    switch (token) {
      case 'amount':
        return withGroups(value, '.', ',');
      case 'amount_no_decimals':
        return withGroups(value, 0, ',');
      case 'amount_with_comma_separator':
        return withGroups(value, ',', '.');
      case 'amount_no_decimals_with_comma_separator':
        return withGroups(value, 0, '.');
      case 'amount_with_apostrophe_separator':
        return withGroups(value, '.', "'");
      case 'amount_no_decimals_with_space_separator':
        return withGroups(value, 0, ' ');
      case 'amount_with_space_separator':
        return withGroups(value, ',', ' ');
      default:
        return withGroups(value, '.', ',');
    }
  });

  const code = shopSettings.currencyCodeEnabled && shopSettings.currency ? ` ${shopSettings.currency}` : '';
  return `${out}${code}`;
}

/* Shared helpers other bundles (product.js) read off the window so they never
   have to `import` this module under a second URL and double-evaluate it. */
window.__234 = window.__234 || {};
window.__234.formatMoney = formatMoney;

/* ---------- fetch a section's HTML ---------- */
export async function fetchSection(sectionId, url = routes.root || '/') {
  const u = new URL(url, location.origin);
  u.searchParams.set('section_id', sectionId);
  const res = await fetch(u.toString());
  if (!res.ok) throw new Error(`Section ${sectionId} ${res.status}`);
  return res.text();
}

export function replaceFromString(htmlString, selector, into) {
  const doc = new DOMParser().parseFromString(htmlString, 'text/html');
  const next = doc.querySelector(selector);
  const target = into || document.querySelector(selector);
  if (next && target) target.replaceWith(next);
  return next;
}

/* ---------- drawer controller ---------- */
const focusableSel =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

class DrawerController {
  constructor() {
    this.openEl = null;
    this.lastFocus = null;
    this.overlay = document.querySelector('[data-overlay]');
    this.bind();
  }

  bind() {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-drawer-open]');
      if (opener) {
        e.preventDefault();
        this.open(opener.getAttribute('data-drawer-open'), opener);
        return;
      }
      if (e.target.closest('[data-drawer-close]')) {
        e.preventDefault();
        this.close();
      }
    });
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.openEl) this.close();
      if (e.key === 'Tab' && this.openEl) this.trap(e);
    });
    document.addEventListener('drawer:open', (e) => this.open(e.detail));
  }

  open(name, trigger) {
    const el = document.querySelector(`[data-drawer="${name}"]`);
    if (!el) return;
    if (this.openEl && this.openEl !== el) this.close({ silent: true });
    this.lastFocus = trigger || document.activeElement;
    this.openEl = el;
    document.body.classList.add('drawer-open');
    this.overlay?.classList.add('is-open');
    el.classList.add('is-open');
    el.removeAttribute('hidden');
    el.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => {
      (el.querySelector('[autofocus]') || el.querySelector(focusableSel) || el).focus();
    });
    el.dispatchEvent(new CustomEvent('drawer:opened', { bubbles: true }));
  }

  close({ silent = false } = {}) {
    const el = this.openEl;
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    this.overlay?.classList.remove('is-open');
    document.body.classList.remove('drawer-open');
    this.openEl = null;
    if (!silent && this.lastFocus) this.lastFocus.focus();
    window.setTimeout(() => el.setAttribute('hidden', ''), 300);
  }

  trap(e) {
    const items = [...this.openEl.querySelectorAll(focusableSel)].filter((n) => n.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

/* Guard against double evaluation: theme.liquid loads theme.js by asset_url
   while product.js imports './theme.js' — if the URLs differ (query string)
   the module body runs twice and every global listener binds twice
   (quantity +2 per click, drawers double-toggle, etc.). */
export const drawers =
  window.__234drawers || (window.__234drawers = new DrawerController());

if (!window.__234booted) {
  window.__234booted = true;

  /* ---------- quantity widget ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.qty button');
    if (!btn) return;
    e.preventDefault();
    /* Belt-and-braces: one physical click shares one Event object across every
       listener on the path, so even if this handler ends up bound twice the
       step only happens once. */
    if (e.__qtyStepped) return;
    e.__qtyStepped = true;
    const input = btn.parentElement.querySelector('input');
    if (!input) return;
    const step = btn.dataset.dir === 'down' ? -1 : 1;
    const min = Number(input.min || 0);
    const cur = parseInt(input.value, 10) || 0;
    const next = Math.max(min, cur + step);
    if (next === cur) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- boot ----------
     Each isolated: a throw in one must not stop the others (a broken
     collection/search init was silently killing motion + sticky header). */
  const boot = [
    ['menu', () => initMenu()],
    ['cart', () => initCart({ drawers, fetchSection, replaceFromString, formatMoney, routes })],
    ['search', () => initSearch({ routes })],
    ['collection', () => initCollection()],
    ['motion', () => initMotion()],
    ['stickyHeader', () => initStickyHeader()],
  ];
  boot.forEach(([name, fn]) => {
    try {
      fn();
    } catch (e) {
      console.error(`[234] ${name} init failed`, e);
    }
  });

  document.documentElement.classList.add('js');
}

/* ---------- sticky header ----------
   CSS pins the header's .shopify-section-group-header-group wrapper (a direct
   child of <body>, so it has room to travel). This makes sure of it from JS
   too, and if an ancestor still breaks native `sticky`, falls back to `fixed`
   + a spacer so the header stays on screen either way. */
function initStickyHeader() {
  const header = document.querySelector('.header');
  if (!header) return;
  if (header.classList.contains('header--static') || header.classList.contains('header--overlay')) return;

  // The wrapper whose containing block is <body> (has room to stay stuck).
  const group =
    header.closest('.shopify-section-group-header-group') || header.closest('.shopify-section');
  if (!group || group === document.body || group.parentElement !== document.body) return;

  const z = (getComputedStyle(document.documentElement).getPropertyValue('--z-header') || '40').trim();
  group.style.position = 'sticky';
  group.style.top = '0';
  group.style.zIndex = z || '40';

  let switched = false;
  const onScroll = () => {
    if (switched) return;
    if (window.scrollY > group.offsetHeight + 40 && group.getBoundingClientRect().top < -1) {
      switched = true;
      window.removeEventListener('scroll', onScroll);
      const spacer = document.createElement('div');
      spacer.setAttribute('data-header-spacer', '');
      spacer.style.height = group.offsetHeight + 'px';
      group.after(spacer);
      group.style.position = 'fixed';
      group.style.left = '0';
      group.style.right = '0';
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
}
